/**
 * Online economy catalogue and pricing maths (pure: shared by server and client UI).
 *
 * The SERVER is the only thing that ever applies these numbers to money or items; the client may use
 * them to render shop panels and the rumour board, nothing more. All places are anchored on POIs from
 * the authored Low Borough layout (`src/world/lowBorough/data.ts`) by id, so they move with the map.
 *
 * Contraband here is fictional game tokens (see shared/items.ts). Production (shared/properties.ts,
 * docs/PROPERTIES.md) is abstract: supplies + server time → a knock-off, never a real-world procedure.
 *
 * Tuning rationale and the source/sink table live in docs/ECONOMY.md.
 */
import { getItemDef, type ItemDef } from './items';

// ----------------------------------------------------------------------------- districts

export const DISTRICT_IDS = ['cinder-yards', 'the-parade', 'tanners-row', 'mercy-square', 'gullet-basin'] as const;
export type EconDistrictId = (typeof DISTRICT_IDS)[number];

/** Mirrors `policePresence` in the authored district data (0..1): the baseline police attention. */
export const DISTRICT_POLICE_PRESENCE: Record<EconDistrictId, number> = {
  'cinder-yards': 0.2,
  'the-parade': 0.65,
  'tanners-row': 0.3,
  'mercy-square': 0.4,
  'gullet-basin': 0.25,
};

/**
 * What each district's buyers pay relative to the reference value (taste, local scarcity). Every
 * good has a best and a worst district so moving goods ACROSS town is where the margin is.
 */
export const DISTRICT_PREFERENCE: Record<EconDistrictId, Record<string, number>> = {
  // Produced goods (production properties) last: tape decks sell up town, tax discs where the cars are, spirits
  // by the pubs and the docks, cigarettes in the terraces.
  'cinder-yards': { velvet: 0.85, 'blue-tins': 1.15, 'ember-gin': 0.9, 'moth-silk': 0.95, 'hot-goods': 1.2, 'tape-decks': 0.85, 'tax-discs': 1.2, 'cut-spirits': 0.9, 'hooky-cigs': 1.1 },
  'the-parade': { velvet: 1.25, 'blue-tins': 1.0, 'ember-gin': 1.1, 'moth-silk': 0.9, 'hot-goods': 1.0, 'tape-decks': 1.25, 'tax-discs': 0.9, 'cut-spirits': 1.15, 'hooky-cigs': 0.9 },
  'tanners-row': { velvet: 0.95, 'blue-tins': 1.25, 'ember-gin': 1.0, 'moth-silk': 1.2, 'hot-goods': 0.9, 'tape-decks': 1.0, 'tax-discs': 1.0, 'cut-spirits': 0.95, 'hooky-cigs': 1.25 },
  'mercy-square': { velvet: 1.1, 'blue-tins': 0.9, 'ember-gin': 1.2, 'moth-silk': 1.3, 'hot-goods': 1.0, 'tape-decks': 1.15, 'tax-discs': 0.85, 'cut-spirits': 1.0, 'hooky-cigs': 1.0 },
  'gullet-basin': { velvet: 1.0, 'blue-tins': 0.85, 'ember-gin': 1.3, 'moth-silk': 0.9, 'hot-goods': 1.1, 'tape-decks': 0.9, 'tax-discs': 1.15, 'cut-spirits': 1.3, 'hooky-cigs': 0.85 },
};

// ----------------------------------------------------------------------------- capacity

/** Storage units a character can carry on foot (item `size` counts; equipment doesn't). */
export const CARRY_CAPACITY = 6;
/** Extra on-foot units while carrying a holdall. */
export const HOLDALL_BONUS = 6;

/** Boot capacity in units per vehicle spec. Police cars can't be loaded (evidence lockers). */
export const VEHICLE_CAPACITY: Record<string, number> = {
  compact: 8,
  coupe: 6,
  saloon: 16,
  estate: 28,
  exec: 18,
  roadster: 4,
  cab: 12,
  van: 40,
  lorry: 60,
  police: 0,
  policevan: 0,
};

export const vehicleCapacity = (specId: string): number => VEHICLE_CAPACITY[specId] ?? 8;

/** Units an item stack occupies (equipment is worn, not stowed; rounds pack `perUnit` to a unit). */
export function stackUnits(defId: string, quantity: number): number {
  const def = getItemDef(defId);
  if (!def || def.category === 'equipment') return 0;
  if (def.perUnit) return def.size * Math.ceil(quantity / def.perUnit);
  return def.size * quantity;
}

// ----------------------------------------------------------------------------- places

export type PlaceKind = 'bank' | 'atm' | 'supplier' | 'buyer' | 'garage' | 'respray' | 'lockup' | 'shop' | 'rumours';

export interface Place {
  id: string;
  kind: PlaceKind;
  /** POI id in the authored layout; the server resolves position and district from it. */
  poi: string;
  name: string;
  /** Extra reach beyond the POI's own trigger radius (metres). */
  reach: number;
}

export const BANKS: readonly Place[] = [
  { id: 'bank-savings', kind: 'bank', poi: 'bank-savings', name: 'Kessmouth Savings Bank', reach: 8 },
  { id: 'atm-anchor', kind: 'atm', poi: 'pub-crooked-anchor', name: 'Cashpoint, The Crooked Anchor', reach: 6 },
  { id: 'atm-coalgate', kind: 'atm', poi: 'phone-coalgate', name: 'Cashpoint, Coalgate', reach: 6 },
  { id: 'atm-dock', kind: 'atm', poi: 'phone-dock', name: 'Cashpoint, Dock Road', reach: 6 },
];

/** Deposit fee (sink): banks are cheaper than cashpoints. Withdrawals are free. */
export const BANK_FEE = { bank: { rate: 0.015, min: 2 }, atm: { rate: 0.03, min: 5 } } as const;

export function depositFee(kind: 'bank' | 'atm', amount: number): number {
  const f = BANK_FEE[kind];
  return Math.min(amount, Math.max(f.min, Math.ceil(amount * f.rate)));
}

export interface SupplierDef extends Place {
  kind: 'supplier';
  /** Goods on offer: def id → base cost per unit (Standard quality) before supply pressure. */
  stock: Record<string, number>;
}

export const SUPPLIERS: readonly SupplierDef[] = [
  { id: 'supplier-bonded', kind: 'supplier', poi: 'docks-bonded-3', name: 'Night man at Bonded Warehouse No. 3', reach: 6, stock: { velvet: 130, 'ember-gin': 72 } },
  { id: 'supplier-lamp', kind: 'supplier', poi: 'club-velvet-lamp', name: 'Back door of The Velvet Lamp', reach: 6, stock: { 'blue-tins': 22 } },
  { id: 'supplier-gorringe', kind: 'supplier', poi: 'scrapyard-gorringe', name: "Gorringe's back shed", reach: 4, stock: { 'moth-silk': 50, 'ember-gin': 76 } },
];

/**
 * Black-market arms (weapons online, docs/WEAPONS.md §13): suppliers like the others (cash only,
 * supply pressure, a money SINK through `supplier-purchase`), but kept out of SUPPLIERS because nobody
 * buys these back (no buyer, no rumour-board price). Unit prices; rounds are priced per round.
 */
export const ARMS_DEALERS: readonly SupplierDef[] = [
  {
    id: 'arms-marlowe',
    kind: 'supplier',
    poi: 'scrapyard-gorringe',
    name: "Sid Marlowe's tea chest",
    reach: 4,
    stock: { 'wpn-cosh': 30, 'wpn-bat': 40, 'wpn-crowbar': 25, 'wpn-knife': 30, 'wpn-revolver': 450, 'wpn-sawnoff': 400, 'ammo-38': 4, 'ammo-12': 5 },
  },
];

/** Every supplier `supplierBuy` accepts (contraband suppliers + arms dealers). */
export const ALL_SUPPLIERS: readonly SupplierDef[] = [...SUPPLIERS, ...ARMS_DEALERS];

export type BuyerType = 'street' | 'fence';

export interface BuyerTypeDef {
  /** Price multiplier on top of district preference. */
  priceMul: number;
  /** Max units of one good per sale. */
  maxQty: number;
  /** Heat per sale: flat + per-unit × item suspicion (scaled by district attention). */
  heatFlat: number;
  heatPerSuspicion: number;
}

export const BUYER_TYPES: Record<BuyerType, BuyerTypeDef> = {
  /** Small quantities, poorer price, barely noticed. */
  street: { priceMul: 0.92, maxQty: 3, heatFlat: 0, heatPerSuspicion: 0.5 },
  /** Bulk, better price — and a lot more attention. */
  fence: { priceMul: 1.12, maxQty: 30, heatFlat: 5, heatPerSuspicion: 1.1 },
};

export interface BuyerDef extends Place {
  kind: 'buyer';
  type: BuyerType;
  /** Def ids this buyer takes. */
  accepts: readonly string[];
}

/** Goods made by production properties (shared/properties.ts): fences take all of them, street dealers the small ones. */
const PRODUCED = ['tape-decks', 'tax-discs', 'cut-spirits', 'hooky-cigs'] as const;
const ALL_HOT = ['velvet', 'blue-tins', 'ember-gin', 'moth-silk', 'hot-goods', ...PRODUCED] as const;

export const BUYERS: readonly BuyerDef[] = [
  { id: 'street-parade', kind: 'buyer', type: 'street', poi: 'phone-parade', name: 'Lad by the Parade phone box', reach: 6, accepts: ['blue-tins', 'moth-silk', 'hot-goods', 'velvet', 'tax-discs', 'hooky-cigs'] },
  { id: 'street-tanners', kind: 'buyer', type: 'street', poi: 'phone-tanners', name: 'Bell Street runner', reach: 6, accepts: ['blue-tins', 'moth-silk', 'hot-goods', 'hooky-cigs', 'tape-decks'] },
  { id: 'street-mercy', kind: 'buyer', type: 'street', poi: 'phone-mercy', name: 'Bench regular, Mercy Square', reach: 6, accepts: ['blue-tins', 'moth-silk', 'ember-gin', 'tape-decks'] },
  { id: 'street-dock', kind: 'buyer', type: 'street', poi: 'phone-dock', name: 'Dock Road lookout', reach: 6, accepts: ['blue-tins', 'ember-gin', 'hot-goods', 'tax-discs', 'cut-spirits'] },
  { id: 'fence-crane', kind: 'buyer', type: 'fence', poi: 'docks-crane-quay', name: 'Basin Crane Quay foreman', reach: 6, accepts: ALL_HOT },
  { id: 'fence-gasworks', kind: 'buyer', type: 'fence', poi: 'gasworks-yard', name: 'Gasworks Yard fence', reach: 6, accepts: ALL_HOT },
  { id: 'fence-brannoc', kind: 'buyer', type: 'fence', poi: 'st-brannocs', name: "The verger at St. Brannoc's", reach: 6, accepts: ALL_HOT },
  { id: 'fence-gorringe', kind: 'buyer', type: 'fence', poi: 'scrapyard-gorringe', name: 'Gorringe himself', reach: 4, accepts: ALL_HOT },
];

export const GARAGE: Place = { id: 'garage-halloran', kind: 'garage', poi: 'garage-halloran', name: 'Halloran Motors', reach: 12 };
export const RESPRAY: Place = { id: 'respray-pike', kind: 'respray', poi: 'respray-pike', name: "Pike's Respray", reach: 6 };
export const SHOP: Place = { id: 'shop-anchor', kind: 'shop', poi: 'mission-start-anchor', name: 'The Crooked Anchor back room', reach: 6 };

/** Rumour boards: where prices are overheard. The pub, the club, every phone box, the safehouse. */
export const RUMOUR_POI_KINDS = ['pub', 'club', 'phonebox', 'safehouse'] as const;
export const RUMOUR_REACH = 6;

/** Vehicle forecourt prices (Halloran Motors). Police cars are not for sale. */
export const VEHICLE_PRICES: Record<string, number> = {
  compact: 2_400,
  saloon: 5_800,
  cab: 4_900,
  van: 8_500,
  coupe: 17_500,
  estate: 4_200,
  roadster: 9_500,
  exec: 14_000,
  lorry: 21_000,
};
/** Cap on garage-owned vehicles per character (storage is a sink too). Halloran's loaner does not count. */
export const MAX_OWNED_VEHICLES = 4;

/**
 * Halloran's loaner (docs/ECONOMY.md "Halloran's loaner", WP-N2): every new character finds a tired compact
 * parked by the spawn — an owned `vehicles` row flagged `loaner`, so it is stored, spawned and repaired like
 * any owned car but is never worth money: Pike won't work on it (no `keep`, no mods), it never counts towards
 * the owned-car cap, and there is no way to sell a car online. Lost (written off, impounded) it can be
 * fetched again at Halloran Motors once `cooldownMs` has passed since the last one was handed over — but only
 * while you have no car of your own.
 */
export const LOANER = { spec: 'compact', paint: 0x8a7a5c, cooldownMs: 10 * 60_000, name: 'Halloran’s loaner' } as const;

/**
 * The kerb bay just south of the player spawn, facing along Coalgate (offsets from the spawn point). Offline the
 * first car to take stands there (src/game/sandbox.ts); online a new character's loaner is parked there, so the
 * first USE a new character sees is their own car, not a stranger's to steal (docs/FIRST-15-MINUTES.md #3).
 */
export const STARTER_BAY = { dx: 3, dz: 1.6, h: Math.PI / 2 } as const;

/** What kind of premises a site is (production properties: which operations fit, docs/PROPERTIES.md). */
export type SiteType = 'lockup' | 'warehouse' | 'rural';

/**
 * Premises with storage and rent (DB `properties.kind = 'lockup'` for all of them). The two railway
 * lockups came first; the warehouse, the old printworks and the smallholding were added with production
 * properties (shared/properties.ts) and work exactly the same way for rent, arrears and repossession.
 */
export interface LockupDef extends Place {
  kind: 'lockup';
  /** Purchase price — or, for a site you `take`, the key money paid to whoever held it before. */
  price: number;
  /** Storage units. */
  capacity: number;
  /** Rent per upkeep period. */
  upkeep: number;
  site: SiteType;
  /** 'buy' from the landlord / estate agent; 'take' over (key money, no questions — and no paperwork to hide behind). */
  acquire: 'buy' | 'take';
  /** Raid-risk multiplier of the site itself (remote = lower, under the law's nose = higher). */
  risk: number;
}

export const LOCKUPS: readonly LockupDef[] = [
  { id: 'lockup-arch-14', kind: 'lockup', poi: 'lockup-arch-14', name: 'Arch 14 Lockup', reach: 6, price: 12_000, capacity: 80, upkeep: 250, site: 'lockup', acquire: 'buy', risk: 1 },
  { id: 'lockup-pilcher-7', kind: 'lockup', poi: 'lockup-pilcher-7', name: 'Lockup 7, Pilcher Ginnel', reach: 6, price: 15_000, capacity: 100, upkeep: 300, site: 'lockup', acquire: 'buy', risk: 1 },
  { id: 'warehouse-gullet-wharf', kind: 'lockup', poi: 'warehouse-gullet-wharf', name: 'Gullet Wharf Co. warehouse', reach: 8, price: 22_000, capacity: 200, upkeep: 380, site: 'warehouse', acquire: 'buy', risk: 1 },
  { id: 'printworks-moss-yard', kind: 'lockup', poi: 'printworks-moss-yard', name: "Old printworks, Moss's Yard", reach: 6, price: 7_500, capacity: 90, upkeep: 200, site: 'lockup', acquire: 'take', risk: 1.25 },
  { id: 'smallholding-slagbank', kind: 'lockup', poi: 'smallholding-slagbank', name: 'Slagbank smallholding', reach: 10, price: 8_000, capacity: 140, upkeep: 220, site: 'rural', acquire: 'take', risk: 0.8 },
];
/** Most premises one character may hold at once (sites are scarce: one tenant each). */
export const MAX_SITES_PER_CHARACTER = 3;
/** Upkeep period (real time). Missed periods accrue as arrears (capped); in arrears = no new deposits. */
export const UPKEEP_PERIOD_MS = 24 * 60 * 60 * 1000;
export const MAX_ARREARS_PERIODS = 7;
/**
 * Repossession: once the arrears sit at the cap, each further unpaid period counts; after this many the
 * lockup is repossessed (owner cleared, re-rentable) and its contents go to the landlord's impound at
 * the same door, released to the old tenant once the arrears are paid (docs/ECONOMY.md, ECON-35).
 */
export const REPOSSESS_AFTER_CAP_PERIODS = 3;

export interface ShopItem {
  defId: string;
  price: number;
  /** Max one per character for equipment. */
  unique: boolean;
}

export const SHOP_ITEMS: readonly ShopItem[] = [
  { defId: 'holdall', price: 350, unique: true },
  { defId: 'police-scanner', price: 600, unique: true },
  { defId: 'tailored-suit', price: 1_500, unique: true },
];

/** Same formulas as single-player (src/economy/wallet.ts) so the feel carries over. */
export const round5 = (n: number): number => Math.max(0, Math.round(n / 5) * 5);
export function repairCost(health: number, maxHealth: number): number {
  const missing = Math.max(0, maxHealth - health);
  if (missing < 1) return 0;
  return round5(15 + (missing / Math.max(1, maxHealth)) * 120);
}
export function resprayCost(heat: number): number {
  return 100 + 60 * Math.max(0, Math.min(4, Math.round(heat)));
}
export function bustedFine(money: number): number {
  return Math.min(Math.max(0, money), Math.max(25, Math.min(600, round5(money * 0.12))));
}

// ----------------------------------------------------------------------------- jobs

export type JobKind = 'courier' | 'recovery';

export const JOB_TUNING = {
  courier: { base: 60, perMetre: 0.45, minDistance: 120 },
  recovery: { base: 110, perMetre: 0.5, minDistance: 150 },
  /** Fastest plausible average speed over the straight-line distance (m/s): faster = rejected. */
  maxAverageSpeed: 32,
  /** Grace added to the minimum duration (pickup fumbling, network). */
  minDurationSlackS: 3,
  /** Time allowed: base + per metre. */
  timeLimitBaseS: 90,
  timeLimitPerMetreS: 0.25,
  /** Diminishing returns: each job completed in the last hour cuts pay by this much (floor below). */
  repeatPenalty: 0.08,
  repeatFloor: 0.45,
  repeatWindowMs: 60 * 60 * 1000,
  offersPerBoard: 3,
  offerTtlMs: 3 * 60 * 1000,
} as const;

export function jobReward(kind: JobKind, distance: number, recentJobs: number): number {
  const t = JOB_TUNING[kind];
  const raw = t.base + t.perMetre * distance;
  const mul = Math.max(JOB_TUNING.repeatFloor, 1 - JOB_TUNING.repeatPenalty * recentJobs);
  return Math.max(5, round5(raw * mul));
}

export function jobMinDurationMs(distance: number): number {
  return Math.max(0, (distance / JOB_TUNING.maxAverageSpeed - JOB_TUNING.minDurationSlackS) * 1000);
}

export function jobTimeLimitMs(distance: number): number {
  return (JOB_TUNING.timeLimitBaseS + JOB_TUNING.timeLimitPerMetreS * distance) * 1000;
}

// ----------------------------------------------------------------------------- markets

export const MARKET_TUNING = {
  /** Demand pressure decays with this e-folding time (recovery after heavy selling). */
  demandRecoveryMs: 10 * 60 * 1000,
  /** Supplier pressure (prices rise when bought out) decays with this. */
  supplyRecoveryMs: 12 * 60 * 1000,
  /** District heat (recent police attention from crimes there) decays with this. */
  districtHeatDecayMs: 8 * 60 * 1000,
  /** Price never falls below this share of the undisturbed price. */
  demandFloor: 0.3,
  /** Up to this price cut when a district is at full police attention. */
  policeDiscount: 0.35,
  /** District heat points that add 1.0 to attention. */
  districtHeatScale: 250,
  /** Supplier price rises up to +60% when bought out. */
  supplyMaxMarkup: 0.6,
} as const;

/** Units of a good one district absorbs before its price halves (demand saturation). */
export const DEMAND_SATURATION: Record<string, number> = {
  velvet: 14,
  'blue-tins': 120,
  'ember-gin': 24,
  'moth-silk': 30,
  'hot-goods': 30,
  // Produced goods: deep markets (a day's output spread over a few buyers sells near full price; a whole
  // warehouse dumped on one fence does not).
  'tape-decks': 40,
  'tax-discs': 60,
  'cut-spirits': 30,
  'hooky-cigs': 90,
};
/** Units one supplier sells before its price has risen 50 %. */
export const SUPPLY_SATURATION: Record<string, number> = {
  velvet: 40,
  'blue-tins': 300,
  'ember-gin': 60,
  'moth-silk': 70,
  // Guns are scarce: a couple bought out of the same chest and Sid's price climbs fast.
  'wpn-revolver': 6,
  'wpn-sawnoff': 6,
  'ammo-38': 400,
  'ammo-12': 300,
};

// ----------------------------------------------------------------------------- quality-tier demand (P3, docs/MARKETS.md)

/** Quality tier index of a stack (Standard 0, Fine 1, Prime 2). Anything else (a courier parcel's run tag) counts as Standard. */
export type QualityTier = 0 | 1 | 2;
export const qualityTier = (quality: number): QualityTier => (quality === 1 ? 1 : quality === 2 ? 2 : 0);

/** Kinds of buyer in the markets: the district buyers (street, fence), the big contested buyers and the temporary high-value ones. */
export type MarketBuyerKind = 'street' | 'fence' | 'big' | 'temp';

/**
 * What a better grade fetches, per kind of buyer (replaces the flat `QUALITY_TIERS.valueMul` in the market).
 * A lad on a corner barely cares; a fence pays the catalogue premium (unchanged since the MVP); the big
 * buyers and the flash ones from up West pay more for the good stuff.
 */
export const QUALITY_PREMIUM: Record<MarketBuyerKind, readonly [number, number, number]> = {
  street: [1, 1.15, 1.3],
  fence: [1, 1.35, 1.8],
  big: [1, 1.3, 1.6],
  temp: [1, 1.45, 1.9],
};
/** Each grade's market is its own, and the better ones are SMALLER: units before the price halves = saturation × this. */
export const QUALITY_SATURATION: readonly [number, number, number] = [1, 0.7, 0.45];
/** Share of the OTHER grades' recent sales that also weighs on a grade (a glut of Standard dents Prime a little). */
export const QUALITY_SPILL = 0.35;

/** Price multiplier for a demand pressure (units recently sold here). 1 → floor. Better grades saturate sooner. */
export function demandFactor(pressure: number, defId: string, tier: QualityTier = 0): number {
  const sat = (DEMAND_SATURATION[defId] ?? 20) * QUALITY_SATURATION[tier];
  return Math.max(MARKET_TUNING.demandFloor, 1 / (1 + Math.max(0, pressure) / sat));
}

/** 0..1 police attention in a district from its baseline presence and recent crime heat there. */
export function policeAttention(district: EconDistrictId, districtHeat: number): number {
  return Math.min(1, DISTRICT_POLICE_PRESENCE[district] * 0.6 + Math.max(0, districtHeat) / MARKET_TUNING.districtHeatScale);
}

export function policeFactor(attention: number): number {
  return 1 - MARKET_TUNING.policeDiscount * Math.min(1, Math.max(0, attention));
}

/** Exponential decay of a pressure value over `dtMs`. */
export function decay(value: number, dtMs: number, tauMs: number): number {
  if (value <= 0 || dtMs <= 0) return Math.max(0, value);
  const v = value * Math.exp(-dtMs / tauMs);
  return v < 1e-3 ? 0 : v;
}

export interface SaleQuote {
  /** Total payout in whole pounds. */
  total: number;
  /** Price of the first unit (what the rumour board shows). */
  unitFirst: number;
  /** Heat points this sale generates (before police witnesses / crew sharing). */
  heat: number;
}

/**
 * Quote selling `qty` units. Each unit sold pushes the local demand pressure up by one, so dumping a
 * van-load in one place yields far less than spreading it around (quantity slippage, anti-farm).
 */
export function quoteSale(args: {
  defId: string;
  quality: number;
  qty: number;
  buyerType: BuyerType;
  district: EconDistrictId;
  /** Demand pressure on THIS grade here (own sales + spill from the other grades, see Market.demand). */
  pressure: number;
  districtHeat: number;
  /** Price bonus from the seller's standing with this district's buyers (0..STANDING.maxBonus, docs/MARKETS.md §5). */
  standingBonus?: number;
  /** The Kessmouth week's price multiplier for this sale (shared/week.ts `weekPriceMul`; bounded there). */
  priceMul?: number;
  /** Police attention the week's modifier adds here (shared/week.ts `weekAttention`). */
  attentionAdd?: number;
}): SaleQuote {
  const def = getItemDef(args.defId) as ItemDef;
  const bt = BUYER_TYPES[args.buyerType];
  const tier = qualityTier(args.quality);
  const q = QUALITY_PREMIUM[args.buyerType][tier];
  const pref = DISTRICT_PREFERENCE[args.district][args.defId] ?? 1;
  const attention = Math.min(1, policeAttention(args.district, args.districtHeat) + Math.max(0, args.attentionAdd ?? 0));
  const base = def.baseValue * q * pref * bt.priceMul * policeFactor(attention) * (1 + Math.max(0, args.standingBonus ?? 0)) * Math.max(0, args.priceMul ?? 1);
  let total = 0;
  let first = 0;
  for (let i = 0; i < args.qty; i++) {
    const unit = base * demandFactor(args.pressure + i, args.defId, tier);
    if (i === 0) first = unit;
    total += unit;
  }
  const heat = (bt.heatFlat + bt.heatPerSuspicion * def.suspicion * args.qty) * (0.5 + attention);
  return { total: Math.floor(total), unitFirst: Math.floor(first), heat };
}

/** Supplier price for `qty` units given its recent sales pressure (each unit bought raises it). */
export function quotePurchase(args: { baseCost: number; defId: string; qty: number; pressure: number }): { total: number; unitFirst: number } {
  const sat = SUPPLY_SATURATION[args.defId] ?? 40;
  let total = 0;
  let first = 0;
  for (let i = 0; i < args.qty; i++) {
    const markup = Math.min(MARKET_TUNING.supplyMaxMarkup, (args.pressure + i) / sat / 2);
    const unit = args.baseCost * (1 + markup);
    if (i === 0) first = unit;
    total += unit;
  }
  return { total: Math.ceil(total), unitFirst: Math.ceil(first) };
}
